import { useState } from 'react';
import { Button, Input, message, Select, Typography } from 'antd';
import type { UploadFile } from 'antd/es/upload/interface';
import { UploadOutlined } from '@ant-design/icons';
import DocumentUploader from '../../../components/Upload/DocumentUploader';
import { useClients } from '../../../context/ClientContext';
import { uploadConfigurationFile } from '../../../infrastructure/apiClient';
import type { ClientEnvironmentDTO } from '../../../infrastructure/models';
import styles from './ConfigurationUpload.module.scss';

const { TextArea } = Input;
const { Paragraph } = Typography;

const UPLOAD_ACCEPT_STRING = '.zip';
const UPLOAD_ALLOWED_FORMAT_LABEL = 'ZIP';

const isFileAllowed = (fileName?: string): boolean => {
  if (!fileName) return false;
  const extension = fileName.split('.').pop()?.toLowerCase();
  return extension === 'zip';
};

export type ConfigurationUploadPayload = {
  uploadName: string;
  uploadDescription: string;
  uploadEnvironmentId: string;
  files: File[];
};

interface ConfigurationUploadProps {
  onSubmit?: (payload: ConfigurationUploadPayload) => Promise<void> | void;
  environments?: ClientEnvironmentDTO[];
  environmentsLoading?: boolean;
}

const ConfigurationUpload = ({ onSubmit, environments = [], environmentsLoading = false }: ConfigurationUploadProps) => {
  const { activeClient } = useClients();
  const [uploadName, setUploadName] = useState('');
  const [uploadDescription, setUploadDescription] = useState('');
  const [uploadEnvironmentId, setUploadEnvironmentId] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const handleFilesChange = (files: File[], nextFileList: UploadFile[]) => {
    const validFiles: File[] = [];
    const filteredFileList: UploadFile[] = [];
    let hasInvalidType = false;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const uploadFile = nextFileList[i];

      if (!isFileAllowed(file.name)) {
        hasInvalidType = true;
        continue;
      }

      validFiles.push(file);
      if (uploadFile) {
        filteredFileList.push(uploadFile);
      }
    }

    if (hasInvalidType) {
      message.error(`Unsupported file type. Allowed formats: ${UPLOAD_ALLOWED_FORMAT_LABEL}.`);
    }

    setSelectedFiles(validFiles);
    setFileList(filteredFileList);
  };

  const handleSubmit = async () => {
    if (!uploadName.trim()) {
      message.warning('Enter an upload name.');
      return;
    }

    if (!uploadEnvironmentId) {
      message.warning('Select an upload environment.');
      return;
    }

    if (selectedFiles.length === 0) {
      message.warning('Select a ZIP file before uploading.');
      return;
    }

    if (!activeClient?.id) {
      message.warning('Select a tenant before uploading.');
      return;
    }

    try {
      setSubmitting(true);
      await uploadConfigurationFile(
        activeClient.id,
        uploadEnvironmentId,
        uploadName.trim(),
        uploadDescription.trim(),
        selectedFiles,
      );
      await onSubmit?.({
        uploadName: uploadName.trim(),
        uploadDescription: uploadDescription.trim(),
        uploadEnvironmentId,
        files: selectedFiles,
      });
      setUploadName('');
      setUploadDescription('');
      setUploadEnvironmentId('');
      setSelectedFiles([]);
      setFileList([]);
    } catch {
      message.error('Failed to upload configuration.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.fieldGroup}>
        <div className={styles.field}>
          <label className={styles.label}>
            Upload Name<span className={styles.required}>*</span>
          </label>
          <Input
            placeholder="Enter upload name"
            value={uploadName}
            onChange={(e) => setUploadName(e.target.value)}
            disabled={submitting}
            className={styles.input}
            maxLength={200}
          />
        </div>

        <div className={styles.field}>
          <label className={styles.label}>Upload Description</label>
          <TextArea
            placeholder="Enter upload description"
            value={uploadDescription}
            onChange={(e) => setUploadDescription(e.target.value)}
            disabled={submitting}
            className={styles.textArea}
            rows={3}
            maxLength={1000}
          />
        </div>

        <div className={styles.field}>
          <label className={styles.label}>
            Upload Environment<span className={styles.required}>*</span>
          </label>
          <Select
            placeholder="Select environment"
            value={uploadEnvironmentId || undefined}
            onChange={(value) => setUploadEnvironmentId(value ?? '')}
            disabled={submitting}
            loading={environmentsLoading}
            allowClear
            className={styles.select}
            options={environments.map((env) => ({
              value: env.id,
              label: env.name,
            }))}
          />
        </div>
      </div>

      <Paragraph className="!text-[#9ca3af] !m-0">
        Supported formats: {UPLOAD_ALLOWED_FORMAT_LABEL}
      </Paragraph>

      <div className={styles.uploader}>
        <DocumentUploader
          className={`${styles.dragger} h-full`}
          fileList={fileList}
          accept={UPLOAD_ACCEPT_STRING}
          onFilesChange={handleFilesChange}
          multiple={false}
          maxCount={1}
          disabled={submitting}
        />
      </div>

      <div className={styles.actions}>
        <Button
          type="primary"
          icon={<UploadOutlined />}
          onClick={handleSubmit}
          loading={submitting}
          disabled={submitting}
          className={styles.uploadButton}
        >
          Upload
        </Button>
      </div>
    </div>
  );
};

export default ConfigurationUpload;
